import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import {
  FolderOpen, Plus, Search, Upload, FileText, Image, Video, File,
  Trash2, Download, Share2, Copy, ExternalLink, Eye, Calendar,
  MoreVertical, Edit, Link2, Lock, Unlock, Grid, Layout, BookOpen,
  DollarSign, Presentation, FileSignature, X, Check, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECT_API = `${API}/project-catalog`;

// Category icons mapping
const categoryIcons = {
  floor_plan: Layout,
  fact_sheet: FileText,
  unit_plan: Grid,
  brochure: BookOpen,
  price_list: DollarSign,
  site_photo: Image,
  video: Video,
  presentation: Presentation,
  contract: FileSignature,
  other: File,
};

// Category colors
const categoryColors = {
  floor_plan: 'bg-blue-500',
  fact_sheet: 'bg-green-500',
  unit_plan: 'bg-purple-500',
  brochure: 'bg-orange-500',
  price_list: 'bg-yellow-500',
  site_photo: 'bg-pink-500',
  video: 'bg-red-500',
  presentation: 'bg-indigo-500',
  contract: 'bg-slate-500',
  other: 'bg-gray-500',
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    timeZone: 'Asia/Dubai',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function ProjectCatalog() {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDocShareDialog, setShowDocShareDialog] = useState(false);
  const [selectedDocForShare, setSelectedDocForShare] = useState(null);
  
  // Form states
  const [projectForm, setProjectForm] = useState({ name: '', description: '' });
  const [uploadForm, setUploadForm] = useState({ category: 'other', description: '' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [shareForm, setShareForm] = useState({ expires_in_days: 30, password: '' });
  const [docShareForm, setDocShareForm] = useState({ expires_in_days: 30 });
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${PROJECT_API}/categories`);
        setCategories(response.data.categories || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);
  
  // Fetch projects
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${PROJECT_API}?token=${token}&search=${searchQuery}`);
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery]);
  
  useEffect(() => {
    if (token) {
      fetchProjects();
    }
  }, [token, fetchProjects]);
  
  // Fetch project details
  const fetchProjectDetails = async (projectId) => {
    try {
      const response = await axios.get(`${PROJECT_API}/${projectId}?token=${token}`);
      setSelectedProject(response.data.project);
      setDocuments(response.data.documents || []);
      setShareLinks(response.data.share_links || []);
    } catch (error) {
      console.error('Failed to fetch project details:', error);
      toast.error('Failed to load project details');
    }
  };
  
  // Create project
  const handleCreateProject = async () => {
    if (!projectForm.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    
    try {
      const response = await axios.post(`${PROJECT_API}?token=${token}`, projectForm);
      if (response.data.success) {
        toast.success('Project created successfully');
        setShowCreateProject(false);
        setProjectForm({ name: '', description: '' });
        fetchProjects();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };
  
  // Update project
  const handleUpdateProject = async () => {
    if (!projectForm.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    
    try {
      const response = await axios.put(`${PROJECT_API}/${selectedProject.id}?token=${token}`, projectForm);
      if (response.data.success) {
        toast.success('Project updated successfully');
        setShowEditProject(false);
        fetchProjects();
        fetchProjectDetails(selectedProject.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project');
    }
  };
  
  // Delete project
  const handleDeleteProject = async () => {
    if (!deleteTarget) return;
    
    try {
      const response = await axios.delete(`${PROJECT_API}/${deleteTarget.id}?token=${token}`);
      if (response.data.success) {
        toast.success('Project deleted successfully');
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
        if (selectedProject?.id === deleteTarget.id) {
          setSelectedProject(null);
          setDocuments([]);
        }
        fetchProjects();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };
  
  // Upload document
  const handleUploadDocument = async () => {
    if (!uploadFile || !selectedProject) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);
      
      const response = await axios.post(
        `${PROJECT_API}/${selectedProject.id}/documents?token=${token}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          },
        }
      );
      
      if (response.data.success) {
        toast.success('Document uploaded successfully');
        setShowUploadDoc(false);
        setUploadFile(null);
        setUploadForm({ category: 'other', description: '' });
        fetchProjectDetails(selectedProject.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Delete document
  const handleDeleteDocument = async (docId) => {
    try {
      const response = await axios.delete(
        `${PROJECT_API}/${selectedProject.id}/documents/${docId}?token=${token}`
      );
      if (response.data.success) {
        toast.success('Document deleted');
        fetchProjectDetails(selectedProject.id);
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };
  
  // Download document
  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(
        `${PROJECT_API}/${selectedProject.id}/documents/${doc.id}/download?token=${token}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };
  
  // Create share link
  const handleCreateShareLink = async () => {
    try {
      const response = await axios.post(
        `${PROJECT_API}/${selectedProject.id}/share?token=${token}`,
        shareForm
      );
      
      if (response.data.success) {
        const shareUrl = `${window.location.origin}/shared/${response.data.share_link.token}`;
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Share link created and copied to clipboard!');
        setShowShareDialog(false);
        setShareForm({ expires_in_days: 30, password: '' });
        fetchProjectDetails(selectedProject.id);
      }
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };
  
  // Copy share link
  const copyShareLink = async (linkToken) => {
    const shareUrl = `${window.location.origin}/shared/${linkToken}`;
    await navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };
  
  // Delete share link
  const handleDeleteShareLink = async (linkId) => {
    try {
      await axios.delete(`${PROJECT_API}/${selectedProject.id}/share-links/${linkId}?token=${token}`);
      toast.success('Share link deleted');
      fetchProjectDetails(selectedProject.id);
    } catch (error) {
      toast.error('Failed to delete share link');
    }
  };
  
  // Create document share link
  const handleCreateDocShareLink = async () => {
    if (!selectedDocForShare || !selectedProject) return;
    
    try {
      const response = await axios.post(
        `${PROJECT_API}/${selectedProject.id}/documents/${selectedDocForShare.id}/share?token=${token}`,
        docShareForm
      );
      
      if (response.data.success) {
        const shareUrl = `${window.location.origin}/shared/doc/${response.data.share_link.token}`;
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Document share link created and copied to clipboard!');
        setShowDocShareDialog(false);
        setSelectedDocForShare(null);
        setDocShareForm({ expires_in_days: 30 });
      }
    } catch (error) {
      toast.error('Failed to create document share link');
    }
  };
  
  // Copy document share link
  const copyDocShareLink = async (linkToken) => {
    const shareUrl = `${window.location.origin}/shared/doc/${linkToken}`;
    await navigator.clipboard.writeText(shareUrl);
    toast.success('Document link copied to clipboard!');
  };
  
  // Get category info
  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || { name: 'Other', icon: 'file' };
  };
  
  return (
    <div className="space-y-6" data-testid="project-catalog">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Project Catalog</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage project documents - floor plans, fact sheets, unit plans and more
          </p>
        </div>
        <Button onClick={() => setShowCreateProject(true)} data-testid="create-project-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>
      
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="search-projects"
        />
      </div>
      
      <div className="grid grid-cols-12 gap-6">
        {/* Projects List */}
        <div className="col-span-12 lg:col-span-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderOpen className="w-5 h-5" />
                Projects ({projects.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No projects yet</p>
                  <p className="text-sm">Create your first project to get started</p>
                </div>
              ) : (
                projects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => fetchProjectDetails(project.id)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedProject?.id === project.id
                        ? 'border-accent bg-accent/5'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                    data-testid={`project-item-${project.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-slate-900 dark:text-white truncate">
                          {project.name}
                        </h3>
                        {project.description && (
                          <p className="text-sm text-slate-500 truncate mt-1">
                            {project.description}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <FileText className="w-3 h-3" />
                            {project.document_count || 0} docs
                          </span>
                          <span>{formatDate(project.created_at)}</span>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(project);
                          setShowDeleteConfirm(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Project Details */}
        <div className="col-span-12 lg:col-span-8">
          {selectedProject ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl">{selectedProject.name}</CardTitle>
                    {selectedProject.description && (
                      <CardDescription className="mt-1">{selectedProject.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setProjectForm({
                          name: selectedProject.name,
                          description: selectedProject.description || '',
                        });
                        setShowEditProject(true);
                      }}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowShareDialog(true)}
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Share
                    </Button>
                    <Button size="sm" onClick={() => setShowUploadDoc(true)} data-testid="upload-doc-btn">
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="documents">
                  <TabsList>
                    <TabsTrigger value="documents">
                      Documents ({documents.length})
                    </TabsTrigger>
                    <TabsTrigger value="share-links">
                      Share Links ({shareLinks.length})
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="documents" className="mt-4">
                    {documents.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No documents yet</p>
                        <p className="text-sm">Upload floor plans, fact sheets, and more</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={() => setShowUploadDoc(true)}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Document
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Group by category */}
                        {categories.map((category) => {
                          const categoryDocs = documents.filter(d => d.category === category.id);
                          if (categoryDocs.length === 0) return null;
                          
                          const Icon = categoryIcons[category.id] || File;
                          
                          return (
                            <div key={category.id}>
                              <h4 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2 flex items-center gap-2">
                                <div className={`w-6 h-6 rounded ${categoryColors[category.id]} flex items-center justify-center`}>
                                  <Icon className="w-3.5 h-3.5 text-white" />
                                </div>
                                {category.name} ({categoryDocs.length})
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                {categoryDocs.map((doc) => (
                                  <div
                                    key={doc.id}
                                    className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    data-testid={`document-${doc.id}`}
                                  >
                                    <div className={`w-10 h-10 rounded-lg ${categoryColors[doc.category]} flex items-center justify-center flex-shrink-0`}>
                                      <Icon className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm text-slate-900 dark:text-white truncate">
                                        {doc.file_name}
                                      </p>
                                      <p className="text-xs text-slate-500">
                                        {formatFileSize(doc.file_size)} • {formatDate(doc.created_at)}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        title="Share document"
                                        onClick={() => {
                                          setSelectedDocForShare(doc);
                                          setShowDocShareDialog(true);
                                        }}
                                      >
                                        <Share2 className="w-4 h-4 text-slate-500" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        title="Download"
                                        onClick={() => handleDownloadDocument(doc)}
                                      >
                                        <Download className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                                        title="Delete"
                                        onClick={() => handleDeleteDocument(doc.id)}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Uncategorized / Other */}
                        {documents.filter(d => !categories.find(c => c.id === d.category)).length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                              Other Documents
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {documents.filter(d => !categories.find(c => c.id === d.category)).map((doc) => (
                                <div
                                  key={doc.id}
                                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700"
                                >
                                  <div className="w-10 h-10 rounded-lg bg-slate-500 flex items-center justify-center">
                                    <File className="w-5 h-5 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-sm truncate">{doc.file_name}</p>
                                    <p className="text-xs text-slate-500">{formatFileSize(doc.file_size)}</p>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownloadDocument(doc)}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="share-links" className="mt-4">
                    {shareLinks.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <Link2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No share links yet</p>
                        <p className="text-sm">Create a shareable link for external access</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={() => setShowShareDialog(true)}
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Create Share Link
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {shareLinks.map((link) => (
                          <div
                            key={link.id}
                            className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 dark:border-slate-700"
                          >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${link.is_active ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                              {link.password_hash ? <Lock className="w-5 h-5" /> : <Unlock className="w-5 h-5" />}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                                  {link.token.substring(0, 20)}...
                                </span>
                                {link.is_active ? (
                                  <Badge variant="outline" className="text-green-600 border-green-200">Active</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-slate-400">Inactive</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {link.view_count} views
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Expires: {link.expires_at ? formatDate(link.expires_at) : 'Never'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyShareLink(link.token)}
                              >
                                <Copy className="w-4 h-4 mr-1" />
                                Copy
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500"
                                onClick={() => handleDeleteShareLink(link.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FolderOpen className="w-16 h-16 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600 dark:text-slate-400">
                  Select a project
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Choose a project from the list to view its documents
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      
      {/* Create Project Dialog */}
      <Dialog open={showCreateProject} onOpenChange={setShowCreateProject}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
            <DialogDescription>
              Add a new project to organize your documents
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                placeholder="e.g., Marina Heights Tower"
                data-testid="project-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={projectForm.description}
                onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                placeholder="Brief description of the project"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateProject(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProject} data-testid="save-project-btn">
              Create Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Project Dialog */}
      <Dialog open={showEditProject} onOpenChange={setShowEditProject}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={projectForm.description}
                onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditProject(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateProject}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Upload Document Dialog */}
      <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Add a document to {selectedProject?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>File *</Label>
              <div className="mt-1">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  {uploadFile ? (
                    <div className="flex items-center gap-3">
                      <FileText className="w-8 h-8 text-accent" />
                      <div>
                        <p className="font-medium">{uploadFile.name}</p>
                        <p className="text-sm text-slate-500">{formatFileSize(uploadFile.size)}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="w-8 h-8 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Click to select a file</p>
                      <p className="text-xs text-slate-400">Any file type, max 50MB</p>
                    </div>
                  )}
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    data-testid="file-input"
                  />
                </label>
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={uploadForm.category}
                onValueChange={(value) => setUploadForm({ ...uploadForm, category: value })}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description of the document"
                rows={2}
              />
            </div>
            {uploading && (
              <div>
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-sm text-slate-500 mt-1">Uploading... {uploadProgress}%</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDoc(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUploadDocument} disabled={!uploadFile || uploading} data-testid="upload-btn">
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Share Link</DialogTitle>
            <DialogDescription>
              Create a shareable link for "{selectedProject?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Link Expiration</Label>
              <Select
                value={String(shareForm.expires_in_days)}
                onValueChange={(value) => setShareForm({ ...shareForm, expires_in_days: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                  <SelectItem value="0">Never expires</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Password Protection (optional)</Label>
              <Input
                type="password"
                value={shareForm.password}
                onChange={(e) => setShareForm({ ...shareForm, password: e.target.value })}
                placeholder="Leave empty for no password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateShareLink}>
              <Share2 className="w-4 h-4 mr-2" />
              Create Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This will permanently delete all documents in this project.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteProject}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Project
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Document Share Dialog */}
      <Dialog open={showDocShareDialog} onOpenChange={setShowDocShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Document</DialogTitle>
            <DialogDescription>
              Create a shareable link for "{selectedDocForShare?.file_name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Link Expiration</Label>
              <Select
                value={String(docShareForm.expires_in_days)}
                onValueChange={(value) => setDocShareForm({ ...docShareForm, expires_in_days: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                  <SelectItem value="0">Never expires</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocShareDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateDocShareLink}>
              <Share2 className="w-4 h-4 mr-2" />
              Create & Copy Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
